// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import {
  Flex,
  Box,
  Accordion,
  Text,
  TextInput,
  Skeleton,
  ActionIcon,
  Stack,
  Group,
  LoadingOverlay,
} from '@mantine/core';
import { IconChevronRight, IconDragDrop, IconDragDrop2, IconTrashX } from '@tabler/icons';
import { FC, useContext, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd-latest';
import { PositionGroupS } from '../../../Service/PositionGroupS';
import {
  BaseDocument,
  DragItem,
  PositionDragItem,
  PositionGroupDragItem,
  PositionGroupDTO,
  PositionGroupHandlers,
  PositionHandlers,
  PositionDto,
} from '../../../Service/DocumentT';
import EmptyText from '../../../Atoms/EmptyText';
import { DocumentS } from '../../../Templates/Documents/DocumentS';
import DragArea from '../../../Atoms/DropArea';
import { ThemeText } from '../../../Atoms/ThemeText';
import LinkText from '../../../Atoms/LinkText';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { openAddProductModal } from './AddProductModal';
import DocumentPosition from './DocumentPositionV2';

interface Props {
  idx: number;
  group: PositionGroupDTO;
  bookitupDocument: BaseDocument;
  groupHandlers: PositionGroupHandlers;
  positionHandlers: PositionHandlers;
  usePreviewVersion: boolean;
  products: PositionDto[];
}

const PositionGroup: FC<Props> = ({
  idx,
  group,
  groupHandlers,
  bookitupDocument,
  positionHandlers,
  usePreviewVersion,
  products,
}) => {
  const { licence } = useJwtClaims();
  const { id } = group;
  const { addGroup, moveGroup, insertNewPositionIntoGroup, removeGroup, addingGroupAtIndex, positionInsertionGroupId } =
    groupHandlers;
  const { insertPositionIntoGroup } = positionHandlers;
  const [name, setName] = useState(group.name);
  const [insertingPosition, setInsertingPosition] = useState(false);
  const { taxType, positions } = bookitupDocument;
  const groupPositions = positions ? positions.filter((position) => position.groupId === id) : [];
  const [value, setValue] = useState<string | null>(id);
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const hasFlowLicence = LicenceS.hasSuitableLicence('FLOW', licence);

  const emptyPositions = groupPositions.length === 0;
  const insertPositionHandler = async () => {
    setInsertingPosition(true);
    try {
      await insertNewPositionIntoGroup(id);
    } finally {
      setInsertingPosition(false);
    }
  };

  const insertProductsHandler = async (productIds: number[], targetPositionId?: number) => {
    const selectedProducts = productIds
      .map((productId) => products.find((product) => product.id === productId))
      .filter((product): product is PositionDto => product !== undefined);

    if (selectedProducts.length !== productIds.length) {
      return false;
    }

    setInsertingPosition(true);
    try {
      for (const product of selectedProducts) {
        // Products are copied through the group endpoint so their new positions keep this group assignment.
        // eslint-disable-next-line no-await-in-loop
        if (!(await insertNewPositionIntoGroup(id, product, targetPositionId))) {
          return false;
        }
      }
      return true;
    } finally {
      setInsertingPosition(false);
    }
  };

  const addProduct = () =>
    executeWithLicenceCheck(LicenceS.Restrictions.PRODUCT_USAGE, () => {
      openAddProductModal({ products, onFinish: insertProductsHandler });
    });

  const groupedPositionHandlers: PositionHandlers = {
    ...positionHandlers,
    addPosition: (targetPositionId, product) => insertNewPositionIntoGroup(id, product, targetPositionId),
    addProducts: insertProductsHandler,
  };

  const [{ canDrop: canDropPosition, isOver: isOverPosition }, dropPositionRef] = useDrop(
    () => ({
      accept: DragItem.POSITION,
      canDrop: (item: PositionDragItem) => item.position.groupId !== id,
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: !!monitor.canDrop(),
      }),
      drop: (item: PositionDragItem, monitor) => {
        if (!monitor.didDrop()) {
          insertPositionIntoGroup(id, item.position.id);
        }
      },
    }),
    [id, insertPositionIntoGroup],
  );

  const [{ isDragging: isDraggingGroup }, dragGroupRef] = useDrag(
    () => ({
      type: DragItem.GROUP,
      item: { group, idx },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [group, id, idx],
  );

  const [{ canDrop: canDropGroup, isOver: isOverGroup }, dropGroupRef] = useDrop(
    () => ({
      accept: DragItem.GROUP,
      canDrop: (item: PositionGroupDragItem) => item.group.id !== id,
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: !!monitor.canDrop(),
      }),
      drop: (item: PositionGroupDragItem) => moveGroup(item.idx, idx),
    }),
    [id, idx, moveGroup],
  );

  const opacity = isDraggingGroup ? 0.5 : 1;
  const opened = value === id;
  const addingGroup = addingGroupAtIndex !== null;

  return (
    <>
      <Flex ref={dropGroupRef} align="center" sx={{ opacity, position: 'relative' }}>
        <LoadingOverlay visible={positionInsertionGroupId === id} overlayBlur={2} loaderProps={{ size: 'lg' }} />
        <div style={{ width: 30 }}>
          <ActionIcon ref={dragGroupRef}>
            {canDropGroup && <IconDragDrop color="#1D5191" size={isOverGroup ? 28 : 20} />}
            {!canDropGroup && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
          </ActionIcon>
        </div>
        <Accordion
          sx={{ flexGrow: 1, opacity }}
          value={value}
          onChange={setValue}
          chevron={<IconChevronRight size="1rem" />}
          styles={{
            chevron: {
              '&[data-rotate]': {
                transform: 'rotate(90deg)',
              },
            },
          }}
          variant="separated"
          defaultValue={id}
          chevronPosition="left"
        >
          <Accordion.Item ref={dropPositionRef} value={id}>
            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
              <Accordion.Control>{!opened && name}</Accordion.Control>
              {opened && (
                <TextInput
                  value={name}
                  onBlur={(e) => PositionGroupS.edit(id, { name: e.currentTarget.value })}
                  onChange={(e) => setName(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  aria-label="Gruppenname"
                  sx={(theme) => ({
                    position: 'absolute',
                    top: '50%',
                    right: theme.spacing.sm * 2 + 28,
                    left: theme.spacing.md / 2 + 24 + theme.spacing.sm,
                    maxWidth: 400,
                    transform: 'translateY(-50%)',
                  })}
                />
              )}
              {!opened && (
                <ThemeText mr={75} weight="bold">
                  {DocumentS.sumUpGroup(groupPositions, taxType as any)}
                </ThemeText>
              )}
              {opened && (
                <ActionIcon
                  color="red"
                  onClick={() => removeGroup(id, name)}
                  sx={(theme) => ({ position: 'absolute', right: theme.spacing.sm, zIndex: 1 })}
                >
                  <IconTrashX size={20} />
                </ActionIcon>
              )}
            </Box>
            <Accordion.Panel>
              {groupPositions.length === 0 && <EmptyText />}
              {groupPositions.length > 0 && (
                <>
                  <Text
                    mb={5}
                    p={6}
                    sx={(theme) => ({
                      backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#4A4A4A',
                      borderRadius: 6,
                      color: 'white',
                      fontSize: 12,
                    })}
                  >
                    <Text span ml="xl">
                      Positions
                    </Text>
                  </Text>
                  <Stack>
                    {groupPositions.map((position) => (
                      <DocumentPosition
                        key={position.id}
                        position={position}
                        bookitupDocument={bookitupDocument}
                        handlers={groupedPositionHandlers}
                        refreshDocument={() => null}
                        previewVersion={usePreviewVersion}
                        products={products}
                      />
                    ))}
                  </Stack>
                </>
              )}
              {insertingPosition && <Skeleton mt="md" height={130} width="100%" />}
              {!canDropPosition && !insertingPosition && emptyPositions && (
                <Group mt="md">
                  <LinkText onClick={insertPositionHandler}>+ Position hinzufügen</LinkText>
                  <LinkText onClick={addProduct}>+ Produkt hinzufügen</LinkText>
                </Group>
              )}
              {canDropPosition && <DragArea isOver={isOverPosition} />}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Flex>
      {hasFlowLicence && (
        <LinkText ml={30} onClick={() => !addingGroup && addGroup(idx + 1)}>
          + Gruppe hinzufügen
        </LinkText>
      )}
      {addingGroupAtIndex === idx + 1 && <Skeleton ml={30} height={56} radius="md" />}
    </>
  );
};

export default PositionGroup;
